import React from "react"

import {ConditionType} from "../../../../../model2/conditions/condition-types"
import FieldsFactory from "../../../../fields-factory"
import {FieldType} from "../../../../field-type"
import ConditionTypeSelector from "../../../../proto/render/condition/condition-type-selector"
import {SelectParent_RenderType} from "../../../../../gd-ui/select-parent-new/@const"
import FieldProject from "../project"
import {FieldConditionFormProps, FieldConditionRenderMode} from "../../../../render/condition/@types"
import Filter from "../../../../../manager/filters/manager/filter"
import ProjectSession from "../../../../../model2/project/project-session"
import ProjectSessionCollection from "../../../../../model2/project/collection"
import SelectParentsNew from "../../../../../gd-ui/select-parent-new/select-parents-new"

// const debug = "FieldProject_FilterForm"
const debug = false

/**
 * Filter form component for selecting projects in filter conditions.
 * Handles both the condition type (INCLUDES/EXCLUDES) and the selection of projects (including children).
 */
export default function FieldProject_FilterForm(props: FieldConditionFormProps) {

    /**
     * Handles changes to the filter condition type (e.g., INCLUDES, EXCLUDES).
     * Updates the condition type and triggers the parent callback.
     */
    const onChangeType = (conditionType: ConditionType) => {
        props.condition.type = conditionType
        props.onChangeCallback(props.condition.type, props.condition.value)
    }

    /**
     * Handles changes to the selected project IDs.
     * For each selected project, adds its ID and all its children project IDs to the filter value.
     * Triggers the parent callback with the updated value.
     */
    const onChangeValue = (projectIds: ID[] | null) => {
        let value: ID[] = []
        _.map(projectIds, (projectId: ID) => {
            const project = gd.session.projects.get(projectId)
            value.push(projectId)
            const childrenIds = project?.childrenProjectsIds
            if (childrenIds) {
                value = value.concat(childrenIds)
            }
        })

        props.condition.value = value
        props.onChangeCallback(props.condition.type, props.condition.value)
    }

    /**
     * Asynchronously collects the set of available projects for selection,
     * depending on the current render mode and filter context.
     * Returns a ProjectSessionCollection containing the available projects.
     */
    const getAvailableProjects = async (): Promise<ProjectSessionCollection | undefined> => {
        let availableProjects: ProjectSessionCollection = new ProjectSessionCollection([])

        switch (props.renderMode) {
            case FieldConditionRenderMode.FILTER_ITEMS_COLLECTION:
            case FieldConditionRenderMode.FILTER_COLLECTION:
                // In filter context, collect the set of project IDs from the filter
                const filter = props.condition as Filter
                const valuesSet = await filter.collectValuesSet()

                // Process the values set to create available projects collection
                if (valuesSet) {
                    const projects: Set<ProjectSession> = new Set<ProjectSession>()

                    // Map project IDs to ProjectSession objects
                    _.map([...valuesSet], (projectId: ID) => {
                        const project = gd.session.projects.get(projectId)
                        if (project) {
                            projects.add(project)

                            _.map(project.parentProjectsIds, (parentProjectId: ID) => {
                                const parentProject = gd.session.projects.get(parentProjectId)
                                parentProject && projects.add(parentProject)
                            })
                        }
                    })

                    // Create collection from found projects
                    availableProjects = new ProjectSessionCollection([...projects])
                }

                break

            case FieldConditionRenderMode.CONDITION:
            default:
                // Not applicable in this mode
                break
        }

        return availableProjects.length > 0 ? availableProjects : undefined
    }

    // The field definition for the project filter
    let field: FieldProject | null | false = null

    switch (props.condition.type) {
        case ConditionType.INCLUDES:
        case ConditionType.EXCLUDES:
            field = FieldsFactory.get({
                type    : FieldType.PROJECTS, // Use the "projects" field type for filters
                fieldId : 'value'
            }) as FieldProject
            break
        default:
            break
    }

    /**
     * Prepare the list of project IDs to display as selected in the UI.
     * Only include projects whose parent projects are not already in the list,
     * to avoid duplicate/child selection when parent is already selected.
     */
    let projectIds: ID[] = []
    _.map(props.condition.value, (projectId: ID) => {
        const project = gd.session.projects.get(projectId)

        if (project) {
            // Check if any parent project is already in the list
            const parentProjectIsAlreadyAdded = _.intersection(projectIds, project.parentProjectsIds).length > 0
            if (!parentProjectIsAlreadyAdded) {
                projectIds.push(projectId)
            }
        }
    })

    debug && console.log(debug, 'render', props)

    return (
        <>
            <ConditionTypeSelector field    = {props.field}
                                   value    = {props.condition.type}
                                   onChange = {onChangeType}/>

            {field &&
                <div className="flex-row-left-center m-l-10">
                    <SelectParentsNew className             = "w-250 flex-no-shrink"
                                      projectIds            = {projectIds}
                                      parentsFor            = {field.parentsFor}
                                      placeholder           = {field.placeholder}
                                      displayWorkspaceRoot  = {field.displayWorkspaceRoot}
                                      getAvailableProjects  = {getAvailableProjects}
                                      renderType            = {SelectParent_RenderType.FORM}
                                      onChange              = {(projectIds: ID[]) => {onChangeValue(projectIds || null)}}/>

                    {/* 
                    // Legacy single-select version, kept for reference:
                    <SelectParentNew className                  = "w-250 flex-no-shrink"
                                     projectId                  = {value}
                                     parentsFor                 = {field.parentsFor}
                                     placeholder                = {field.placeholder}
                                     displayWorkspaceRoot       = {field.displayWorkspaceRoot}
                                     getAvailableProjects       = {getAvailableProjects}
                                     renderType                 = {SelectParent_RenderType.FORM}
                                     onChange                   = {(projectId:ID) => {onChangeValue(projectId || null)}}/>
                    */}
                </div>
            }
        </>
    )
}
